import type { PublicPriceTier } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { activeTierOf, parseOrderQty } from '@/entities/product';
import { STORE_DETAIL_PRICE_TIERS_ID } from '@/shared/config';
import { formatCount, formatKrw } from '@/shared/lib';
import { cn } from '@/shared/ui';

interface PriceTierTableProps {
  /** minQty 오름차순. */
  readonly tiers: readonly PublicPriceTier[];
  /** 우측 패널 수량 입력의 문자열 그대로 — 페이지가 한 상태로 패널과 표를 잇는다. */
  readonly qtyDraft: string;
}

const tierRangeLabel = (tier: PublicPriceTier, next: PublicPriceTier | undefined): string =>
  next === undefined
    ? `${formatCount(tier.minQty)}개 이상`
    : `${formatCount(tier.minQty)} ~ ${formatCount(next.minQty - 1)}개`;

/**
 * 수량별 가격 표 — 우측 패널에서 고른 수량이 속한 구간을 강조한다.
 * 수량 입력·예상 합계는 우측 패널(수량 선택)이 맡는다.
 */
export const PriceTierTable = ({ tiers, qtyDraft }: PriceTierTableProps): ReactElement => {
  const activeTier = activeTierOf(tiers, parseOrderQty(qtyDraft));

  return (
    // 위 바로가기 탭이 이 제목으로 내려온다 — 브라우저는 앵커 대상(제목) 자신의 scroll-margin 만 보므로
    // 붙어 있는 헤더에 가리지 않게 띄우는 여백(scroll-mt-28)은 section 이 아니라 제목에 둔다.
    <section aria-labelledby={STORE_DETAIL_PRICE_TIERS_ID} className="space-y-6">
      <div className="space-y-2">
        <h2
          id={STORE_DETAIL_PRICE_TIERS_ID}
          className="scroll-mt-28 text-2xl leading-tight font-bold tracking-[-0.02em]"
        >
          수량별 가격
        </h2>
        <p className="text-sm text-muted-foreground">
          개당 가격과 예상 합계는 모두 부가세 포함 금액입니다.
        </p>
      </div>

      <section
        // biome-ignore lint/a11y/noNoninteractiveTabindex: 좁은 폭에서 표가 넘칠 때 키보드만으로 잘린 열을 훑을 수 있어야 한다(WCAG 2.1.1)
        tabIndex={0}
        aria-label="수량별 가격 표"
        className="overflow-x-auto border-t border-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <table className="w-full text-sm">
          <thead className="text-left text-muted-foreground">
            <tr className="border-b border-foreground/10">
              <th className="px-4 py-3 font-medium">수량 구간</th>
              <th className="px-4 py-3 text-right font-medium">개당 가격</th>
              <th className="px-4 py-3 text-right font-medium">예상 합계</th>
            </tr>
          </thead>
          <tbody>
            {tiers.map((tier, index) => {
              const isActive = activeTier?.minQty === tier.minQty;

              return (
                <tr
                  key={tier.minQty}
                  data-active={isActive || undefined}
                  className={cn(
                    'border-b border-foreground/10',
                    isActive && 'bg-muted/70 font-semibold',
                  )}
                >
                  <td className="px-4 py-3.5">
                    <span className="flex items-center gap-2">
                      {tierRangeLabel(tier, tiers[index + 1])}
                      {/* 지금 고른 수량의 구간 — 색만으로 구분하지 않게 글자 표식을 함께 단다. */}
                      {isActive && (
                        <span className="bg-brand-lime px-1.5 py-0.5 text-[11px] font-semibold text-black">
                          선택 수량
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 text-right tabular-nums">
                    {formatKrw(tier.unitPrice)}
                  </td>
                  <td className="px-4 py-3.5 text-right tabular-nums">
                    {formatKrw(tier.minQty * tier.unitPrice)}부터
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
    </section>
  );
};
